import { BarChart3, ClipboardList, HeartPulse, PackageCheck, Truck, Warehouse } from "lucide-react";
import type { DashboardSlug } from "@/features/analytics/core/types";

/** Base path for KPI dashboards — nested under the dashboard Analytics tab. */
export const DASHBOARD_ANALYTICS_BASE = "/dashboard/analytics";

export const DEFAULT_DASHBOARD_ANALYTICS_SLUG: DashboardSlug = "booking-manifest-control";

export function dashboardAnalyticsHref(slug: DashboardSlug): string {
  return `${DASHBOARD_ANALYTICS_BASE}/${slug}`;
}

export function isDashboardAnalyticsPath(pathname: string): boolean {
  return (
    pathname === DASHBOARD_ANALYTICS_BASE || pathname.startsWith(`${DASHBOARD_ANALYTICS_BASE}/`)
  );
}

export interface AnalyticsNavEntry {
  slug: DashboardSlug;
  label: string;
  description: string;
  defaultPeriod: string;
  icon: typeof BarChart3;
  /** Override the period picker chips for this dashboard (preset keys). Custom date range stays available. */
  periodPresets?: string[];
  /**
   * Cap on a hand-picked custom range. Operational dashboards stay short: the
   * funnel alone fires five aggregations over the status-events spine, and every
   * KPI card on the page widens with it. Defaults to CUSTOM_RANGE_MAX_DAYS.
   */
  customRangeMaxDays?: number;
}

/** Backend ceiling (periodResolver.MAX_RANGE_DAYS) — the default custom cap. */
export const CUSTOM_RANGE_MAX_DAYS = 366;

/** Cap for the operational journey dashboards (D1–D4). */
export const OPERATIONAL_RANGE_MAX_DAYS = 31;

/**
 * Period chips: **1 business day + Custom**, and nothing else (COO 2026-08-16).
 *
 * A business day means two different things depending on what the board
 * measures, so each dashboard names its own preset rather than sharing one:
 *
 *   booking gate  `collection_window_live`  last 11:15 cutoff → now
 *   everything else `today`                 00:00 SAST → now
 *
 * That split is deliberate. Booking is judged against the collection cutoff, so
 * its day runs cutoff-to-cutoff; every downstream stage is judged against the
 * calendar day the work happened on. Both chips read "1 business day" — the
 * reader should not have to know which clock a board is on.
 *
 * The multi-span chips (7d/30d) are gone. Metrics that genuinely need a longer
 * window — promise rate, the stale section, churn, aging — pin it themselves in
 * the catalog via `periodInheritsView: false`, so they are unaffected by this.
 */
export const BUSINESS_DAY_CUTOFF = "collection_window_live";
export const BUSINESS_DAY_CALENDAR = "today";

/** Presets a dashboard offers = its own business day. Custom is always available. */
const businessDay = (preset: string) => [preset];

/**
 * The 5 journey dashboards, in parcel-journey stage order — the primary
 * analytics nav (rendered by JourneyRail).
 */
export const JOURNEY_DASHBOARD_SLUGS: DashboardSlug[] = [
  "booking-manifest-control",
  "collection-control",
  "hub-sort-control",
  "delivery-control",
  "merchant-health",
];

export const analyticsNavEntries: AnalyticsNavEntry[] = [
  {
    slug: "booking-manifest-control",
    label: "Booking & Manifest",
    description:
      "Shipments booked, eligible-not-booked, manifests, and the 11:15 collection cutoff.",
    // Booking is judged against the 11:15 cutoff, so its business day is
    // cutoff-to-now, not midnight-to-now.
    defaultPeriod: BUSINESS_DAY_CUTOFF,
    periodPresets: businessDay(BUSINESS_DAY_CUTOFF),
    customRangeMaxDays: OPERATIONAL_RANGE_MAX_DAYS,
    icon: ClipboardList,
  },
  {
    slug: "collection-control",
    label: "Collection",
    description: "Collection drivers, manifests in progress, completed, and not yet started.",
    defaultPeriod: BUSINESS_DAY_CALENDAR,
    periodPresets: businessDay(BUSINESS_DAY_CALENDAR),
    customRangeMaxDays: OPERATIONAL_RANGE_MAX_DAYS,
    icon: PackageCheck,
  },
  {
    slug: "hub-sort-control",
    label: "Hub & Sort",
    description: "Shipments received, sort progress, and average hub dwell time.",
    defaultPeriod: BUSINESS_DAY_CALENDAR,
    periodPresets: businessDay(BUSINESS_DAY_CALENDAR),
    customRangeMaxDays: OPERATIONAL_RANGE_MAX_DAYS,
    icon: Warehouse,
  },
  {
    slug: "delivery-control",
    label: "Delivery",
    description: "Completed, failed, in-progress, success & POD rates, and SLA pressure.",
    defaultPeriod: BUSINESS_DAY_CALENDAR,
    periodPresets: businessDay(BUSINESS_DAY_CALENDAR),
    customRangeMaxDays: OPERATIONAL_RANGE_MAX_DAYS,
    icon: Truck,
  },
  {
    slug: "merchant-health",
    label: "Merchant Health",
    description: "Active, new, churned merchants, balance health, and top-ups (last 30 days).",
    // Account management, not ops: a one-day merchant-health view is
    // meaningless, so this board keeps its 30-day window (COO 2026-08-16).
    // The wider custom cap stays for year-on-year account work.
    defaultPeriod: "30d_window",
    periodPresets: businessDay("30d_window"),
    icon: HeartPulse,
  },
];
